import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search, SlidersHorizontal } from "lucide-react";
import { COUNTRIES } from "@/data/brokers";
import { Eyebrow } from "@/components/shared/Primitives";
import { DirectoryRow } from "@/components/brokers/DirectoryRow";
import "@/components/brokers/directory.css";
import { useCompare } from "@/context/CompareContext";
import { useBrokers } from "@/context/BrokersContext";
import { Reveal } from "@/components/motion/Reveal";

const REGS = ["FCA", "ASIC", "CySEC", "FSCA", "KNF", "DFSA", "Offshore"];
const PLATS = ["MT4", "MT5", "cTrader", "TradingView"];
const SPREADS = ["Raw ECN", "Standard"];
const SORTS = { cost: "Lowest all-in cost", alpha: "Alphabetical", trust: "Trust score", deposit: "Lowest minimum deposit" };
const allIn = (b) => b.eurUsdSpread * 10 + b.commission;

const Group = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="directory-filter-group">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between text-sm font-medium" data-testid={`filter-group-${title.toLowerCase().replace(/\s/g, "-")}`}>{title}<ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
      <AnimatePresence initial={false}>{open && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden"><div className="pt-3 flex flex-wrap gap-2">{children}</div></motion.div>}</AnimatePresence>
    </div>
  );
};

const Chip = ({ on, onClick, children, testid }) => <button onClick={onClick} aria-pressed={on} data-on={on} className="directory-filter-chip" data-testid={testid}>{children}</button>;

export default function Brokers() {
  const [q, setQ] = useState("");
  const [regs, setRegs] = useState([]);
  const [plats, setPlats] = useState([]);
  const [spreads, setSpreads] = useState([]);
  const [swapFree, setSwapFree] = useState(false);
  const [bonus, setBonus] = useState(false);
  const [country, setCountry] = useState("");
  const [sort, setSort] = useState("cost");
  const [showFilters, setShowFilters] = useState(true);
  const { toggle, has } = useCompare();
  const { brokers: BROKERS } = useBrokers();
  const tog = (set) => (v) => set((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const list = useMemo(() => BROKERS.filter((b) => {
    const regList = b.entities.map((e) => e.regulator);
    if (q && !b.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (regs.length && !regs.some((r) => (r === "Offshore" ? regList.some((x) => ["FSA", "SCB", "VFSC", "FSC"].includes(x)) : regList.includes(r)))) return false;
    if (plats.length && !plats.some((p) => b.platforms.includes(p))) return false;
    if (spreads.length && !spreads.some((s) => b.spreadModel.includes(s === "Raw ECN" ? "Raw" : "Standard"))) return false;
    if (swapFree && !b.swapFree) return false;
    if (bonus && !b.depositBonus) return false;
    if (country && !b.entities.some((e) => e.regions.includes(country))) return false;
    return true;
  }).sort((a, b) => sort === "alpha" ? a.name.localeCompare(b.name) : sort === "trust" ? b.trust - a.trust : sort === "deposit" ? a.minDeposit - b.minDeposit : allIn(a) - allIn(b)), [BROKERS, q, regs, plats, spreads, swapFree, bonus, country, sort]);

  return (
    <div className="container-x directory-page" data-testid="brokers-page">
      <Reveal className="directory-page-header">
        <div>
          <Eyebrow className="mb-4">Broker directory</Eyebrow>
          <h1 className="t-h1" data-testid="directory-heading">Browse every verified broker.</h1>
          <p className="mt-4 max-w-xl t-lead">Prefer to explore? Filter by regulation, pricing model, platform and country. Or let the <Link to="/brokers/finder" data-testid="directory-finder-link" className="text-[var(--cobalt)] underline-offset-4 hover:underline">finder</Link> rank them for you.</p>
        </div>
        <div className="directory-toolbar">
          <label className="directory-search-wrap"><Search size={15} aria-hidden="true" /><input aria-label="Search brokers" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brokers" className="control" data-testid="directory-search" /></label>
          <select aria-label="Sort brokers" value={sort} onChange={(e) => setSort(e.target.value)} className="control" data-testid="directory-sort">{Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <button onClick={() => setShowFilters(!showFilters)} aria-label="Toggle filters" aria-expanded={showFilters} className="btn-outline h-11 lg:hidden" data-testid="directory-toggle-filters"><SlidersHorizontal size={15} /></button>
        </div>
      </Reveal>

      <div className="directory-layout">
        <aside className={`directory-sidebar ${showFilters ? "" : "hidden lg:block"}`} data-testid="directory-filters">
          <div className="lg:sticky lg:top-24">
            <p className="eyebrow mb-2">Filters</p>
            <Group title="Regulation">{REGS.map((r) => <Chip key={r} on={regs.includes(r)} onClick={() => tog(setRegs)(r)} testid={`filter-reg-${r.toLowerCase()}`}>{r}</Chip>)}</Group>
            <Group title="Spread model">{SPREADS.map((r) => <Chip key={r} on={spreads.includes(r)} onClick={() => tog(setSpreads)(r)} testid={`filter-spread-${r.split(" ")[0].toLowerCase()}`}>{r}</Chip>)}</Group>
            <Group title="Account features"><Chip on={swapFree} onClick={() => setSwapFree(!swapFree)} testid="filter-swap-free">Swap-free</Chip><Chip on={bonus} onClick={() => setBonus(!bonus)} testid="filter-bonus">Deposit bonus</Chip></Group>
            <Group title="Platforms">{PLATS.map((r) => <Chip key={r} on={plats.includes(r)} onClick={() => tog(setPlats)(r)} testid={`filter-plat-${r.toLowerCase()}`}>{r}</Chip>)}</Group>
            <Group title="Country" defaultOpen={false}><select aria-label="Filter by country" value={country} onChange={(e) => setCountry(e.target.value)} className="control w-full" data-testid="filter-country"><option value="">Any country</option>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></Group>
            <button onClick={() => { setRegs([]); setPlats([]); setSpreads([]); setSwapFree(false); setBonus(false); setCountry(""); setQ(""); }} className="mt-4 text-xs text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="filter-reset">Reset filters</button>
          </div>
        </aside>

        <div className="directory-results">
          <div className="directory-table-panel" data-testid="directory-table-panel">
            <div className="directory-table-scroll" role="region" aria-label="Broker comparison table" tabIndex={0} data-testid="directory-table-scroll">
              <table className="directory-table" aria-label="Broker directory" data-testid="directory-table">
                <colgroup>
                  <col className="directory-col-broker" /><col className="directory-col-regulation" /><col className="directory-col-cost" /><col className="directory-col-deposit" /><col className="directory-col-actions" />
                </colgroup>
                <thead>
                  <tr className="directory-column-head" data-testid="directory-columns">
                    {["Broker", "Regulation", "EUR/USD all-in", "Min dep.", "Actions"].map((label, index) => <th scope="col" key={label} data-testid={`directory-column-${index}`}>{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {list.map((b) => <DirectoryRow key={b.slug} broker={b} selected={has(b.slug)} onCompare={() => toggle(b.slug, b.short)} />)}
                  {list.length === 0 && <tr><td colSpan={5} className="directory-empty-cell" data-testid="directory-empty">No brokers match these filters.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="directory-cost-note" data-testid="directory-cost-note">All-in cost = EUR/USD spread (pips × $10) + round-turn commission per standard lot. Measured, not advertised.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
